import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Calculator, AlertTriangle, CheckCircle2, XCircle, ArrowRight,
  Package, Clock, TrendingUp, Filter, RefreshCw, FileText, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/format';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MRP() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  
  // MRP Data
  const [dashboard, setDashboard] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  
  // Config
  const [planningHorizon, setPlanningHorizon] = useState(30);
  const [minOrderValue, setMinOrderValue] = useState(10000);
  
  // Filters
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pending');

  // Fetch projects on mount
  useEffect(() => {
    fetch(`${API}/api/projects`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const active = (Array.isArray(data) ? data : []).filter(p => p.status !== 'Completed');
        setProjects(active);
        if (active.length > 0) setSelectedProject(active[0].id);
      })
      .catch(console.error);
  }, []);

  // Fetch MRP dashboard when project changes
  const fetchDashboard = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/mrp/dashboard?project_id=${selectedProject}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        setDashboard(await res.json());
      }
    } catch (err) {
      console.error('Error fetching MRP dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ project_id: selectedProject });
      if (priorityFilter) params.append('priority', priorityFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      const res = await fetch(`${API}/api/mrp/suggestions?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, priorityFilter, categoryFilter, statusFilter]);

  useEffect(() => {
    fetchDashboard();
    fetchSuggestions();
  }, [fetchDashboard, fetchSuggestions]);

  // Calculate MRP
  const handleCalculateMRP = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    
    setCalculating(true);
    try {
      const res = await fetch(`${API}/api/mrp/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: selectedProject,
          planning_horizon_days: planningHorizon,
          min_order_value: minOrderValue,
          include_buffer: 0.05,
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`MRP calculated: ${data.suggestions_count} items, ₹${data.total_estimated_value?.toLocaleString('en-IN')}`);
        fetchDashboard();
        fetchSuggestions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'MRP calculation failed');
      }
    } catch (err) {
      toast.error('Error calculating MRP');
    } finally {
      setCalculating(false);
    }
  };

  // Approve suggestion
  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API}/api/mrp/suggestions/${id}/approve`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Suggestion approved');
        fetchSuggestions();
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Error approving suggestion');
    }
  };

  // Reject suggestion
  const handleReject = async (id) => {
    try {
      const res = await fetch(`${API}/api/mrp/suggestions/${id}/reject`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Suggestion rejected');
        fetchSuggestions();
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Error rejecting suggestion');
    }
  };

  // Convert to MPR
  const handleConvertToMPR = async () => {
    const approvedIds = suggestions
      .filter(s => s.status === 'Approved' && selectedSuggestions.includes(s.id))
      .map(s => s.id);
    
    if (approvedIds.length === 0) {
      toast.error('Select approved suggestions to convert');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/mrp/convert-to-mpr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(approvedIds),
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.mprs_created} MPR(s)`);
        setSelectedSuggestions([]);
        fetchSuggestions();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Conversion failed');
      }
    } catch (err) {
      toast.error('Error converting to MPR');
    }
  };

  // Toggle selection
  const toggleSelection = (id) => {
    setSelectedSuggestions(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Select all approved
  const selectAllApproved = () => {
    const approvedIds = suggestions.filter(s => s.status === 'Approved').map(s => s.id);
    setSelectedSuggestions(approvedIds);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-gray-100 text-gray-700';
      case 'Approved': return 'bg-indigo-50 text-indigo-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      case 'Converted': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-6" data-testid="mrp-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#3b4872]" />
            Material Requirement Planning
          </h1>
          <p className="text-sm text-gray-500">Auto-generate MPRs from WBS schedule + BOQ quantities</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="mrp-project-filter"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Select Project</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <Button
            onClick={handleCalculateMRP}
            disabled={calculating || !selectedProject}
            data-testid="calculate-mrp-btn"
            className="bg-[#3b4872] hover:bg-[#2d3856] text-white"
          >
            {calculating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4 mr-2" />
            )}
            Calculate MRP
          </Button>
        </div>
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            MRP Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Planning Horizon (days)</label>
              <Input
                type="number"
                value={planningHorizon}
                onChange={(e) => setPlanningHorizon(parseInt(e.target.value) || 30)}
                className="w-32"
                min={7}
                max={90}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Min Order Value (₹)</label>
              <Input
                type="number"
                value={minOrderValue}
                onChange={(e) => setMinOrderValue(parseInt(e.target.value) || 10000)}
                className="w-32"
                min={0}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dashboard Summary */}
      {dashboard?.has_mrp_data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Total Suggestions</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {dashboard.summary?.total_suggestions || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Estimated Value</div>
                <div className="text-2xl font-bold text-indigo-700 mt-1">
                  {formatCurrency(dashboard.summary?.total_estimated_value)}
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="text-xs text-red-600 uppercase tracking-wide">Critical</div>
                <div className="text-2xl font-bold text-red-700 mt-1">
                  {dashboard.summary?.critical_count || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-indigo-200 bg-indigo-50">
              <CardContent className="pt-4">
                <div className="text-xs text-indigo-600 uppercase tracking-wide">High Priority</div>
                <div className="text-2xl font-bold text-indigo-700 mt-1">
                  {dashboard.summary?.high_count || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Pending Approval</div>
                <div className="text-2xl font-bold text-amber-600 mt-1">
                  {dashboard.summary?.pending_approval || 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Converted to MPR</div>
                <div className="text-2xl font-bold text-green-600 mt-1">
                  {dashboard.summary?.converted_to_mpr || 0}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* By Category */}
          {dashboard.by_category && dashboard.by_category.length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Requirements by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {dashboard.by_category.map(cat => (
                    <div 
                      key={cat.category}
                      className="px-3 py-2 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100"
                      onClick={() => setCategoryFilter(cat.category === categoryFilter ? '' : cat.category)}
                    >
                      <div className="text-sm font-medium">{cat.category}</div>
                      <div className="text-xs text-gray-500">{formatCurrency(cat.value)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Calculator className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No MRP Data</h3>
            <p className="text-gray-500 mb-4">
              Run "Calculate MRP" to generate material requirements based on WBS schedule and BOQ.
            </p>
            <Button onClick={handleCalculateMRP} disabled={!selectedProject} className="bg-[#3b4872] hover:bg-[#2d3856] text-white">
              <Calculator className="w-4 h-4 mr-2" />
              Calculate MRP
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters & Actions */}
      {dashboard?.has_mrp_data && (
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-3">
            <select
              data-testid="mrp-priority-filter"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Priorities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
            <select
              data-testid="mrp-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Converted">Converted</option>
            </select>
            {categoryFilter && (
              <span className="px-3 py-2 bg-indigo-50 text-[#3b4872] rounded-lg text-sm flex items-center gap-2">
                {categoryFilter}
                <button onClick={() => setCategoryFilter('')}>×</button>
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAllApproved}>
              Select All Approved
            </Button>
            <Button 
              size="sm" 
              onClick={handleConvertToMPR}
              disabled={selectedSuggestions.length === 0}
              data-testid="convert-to-mpr-btn"
              className="bg-[#3b4872] hover:bg-[#2d3856] text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              Convert to MPR ({selectedSuggestions.length})
            </Button>
          </div>
        </div>
      )}

      {/* Suggestions List */}
      {suggestions.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="w-10 px-3 py-3"></th>
                    <th className="text-left px-3 py-3 font-medium text-gray-600">Item</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Category</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Priority</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Qty to Order</th>
                    <th className="text-right px-3 py-3 font-medium text-gray-600">Est. Value</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Order By</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {suggestions.map(s => (
                    <tr key={s.id} data-testid={`mrp-row-${s.id}`} className={`hover:bg-slate-50 ${selectedSuggestions.includes(s.id) ? 'bg-indigo-50' : ''}`}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedSuggestions.includes(s.id)}
                          onChange={() => toggleSelection(s.id)}
                          disabled={s.status !== 'Approved'}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-mono text-xs text-gray-500">{s.item_code}</div>
                        <div className="text-sm truncate max-w-xs" title={s.description}>
                          {s.description?.length > 50 ? `${s.description.substring(0, 50)}...` : s.description}
                        </div>
                        <div className="text-xs text-gray-400">{s.linked_task?.name}</div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded">{s.category}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded border ${getPriorityColor(s.priority)}`}>
                          {s.priority}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="font-medium">{s.suggested_order_qty?.toLocaleString('en-IN')}</div>
                        <div className="text-xs text-gray-500">{s.unit}</div>
                      </td>
                      <td className="px-3 py-3 text-right font-medium">
                        {formatCurrency(s.estimated_value)}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className={`text-xs ${s.days_until_order < 0 ? 'text-rose-700 font-medium' : ''}`}>
                          {s.days_until_order < 0 ? (
                            <span className="flex items-center justify-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {Math.abs(s.days_until_order)}d overdue
                            </span>
                          ) : (
                            `${s.days_until_order}d`
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatDate(s.order_by_date)}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(s.status)}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {s.status === 'Pending' && (
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleApprove(s.id)}
                              title="Approve"
                              data-testid={`mrp-approve-${s.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              onClick={() => handleReject(s.id)}
                              title="Reject"
                              data-testid={`mrp-reject-${s.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {s.status === 'Converted' && (
                          <span className="text-xs text-emerald-700">{s.converted_to_mpr_no}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      )}
    </div>
  );
}
